import { Box, Center, Flex, Icon, Stack, VStack } from '@chakra-ui/react';
import { Divider, Text } from '@diamond/shared/ui';
import { DateFormatterIdn } from '@diamond/shared/utils';
import { DateRangeOutlined } from '@mui/icons-material';
import { format } from 'date-fns';
import id from 'date-fns/locale/id';
import Link from 'next/link';
import { memo } from 'react';

import { StatusBadge } from './status-badge';
import { OrderHeaderProps } from './types';

const OrderHeader = memo(
  ({
    data,
    isMobile,
    featureFlagEnabled,
    state,
    stateOption,
  }: OrderHeaderProps) => {
    const defaultDate = format(
      data?.transaction_date,
      'eeee, d MMMM yyyy • HH:mm',
      {
        locale: id,
      }
    );

    const displayDate = featureFlagEnabled
      ? DateFormatterIdn(data.po_effective_date)
      : defaultDate;

    if (isMobile) {
      return (
        <>
          <Stack w="full" spacing="2">
            <Flex alignItems="center" gap="1">
              <Icon as={DateRangeOutlined} color="blue" boxSize="20px" />
              <Text fontSize="2xs">
                {format(data?.transaction_date, 'eeee, d MMMM yyyy • HH:mm', {
                  locale: id,
                })}
              </Text>
            </Flex>
            <Divider />
          </Stack>
          <Flex gap="2" mt="2">
            <Text fontSize="xs" fontWeight="semibold" color="green">
              <Text as="span" fontSize="2xs" color="gray.75">
                PO{' '}
              </Text>
              {data?.sub_po_number}
            </Text>
          </Flex>
          <Center my="2">
            <StatusBadge
              data={data}
              state={state}
              stateOption={stateOption}
              isMobile={true}
            />
          </Center>
        </>
      );
    }

    return (
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={{ base: '1', md: '4' }}
        mb="2"
      >
        <Box display="flex" gap="2">
          <Icon as={DateRangeOutlined} color="blue" />
          <Text fontWeight="semibold" fontSize="md">
            {displayDate}
          </Text>
        </Box>
        <Flex gap="8" alignItems="center">
          <VStack align="flex-start" spacing="0" minW="fit-content">
            <Text fontSize="xs" textColor="gray.75">
              No. PO
            </Text>
            <Text fontWeight="semibold" color="green">
              {data?.invoice_link ? (
                <Link
                  href={data?.invoice_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {data?.transaction_id}
                </Link>
              ) : (
                data?.transaction_id
              )}
            </Text>
          </VStack>
          <VStack align="flex-start" spacing="0" minW="fit-content">
            <Text fontSize="xs" textColor="gray.75" minW="fit-content">
              No. Sub PO
            </Text>
            <Text fontWeight="semibold" color="blue">
              {data?.sub_po_number}
            </Text>
          </VStack>
          <StatusBadge
            data={data}
            state={state}
            stateOption={stateOption}
            isMobile={false}
          />
        </Flex>
      </Flex>
    );
  }
);

OrderHeader.displayName = 'OrderHeader';

export { OrderHeader };
